import { RootState } from "app/store";
import { EControlType } from "components/ControlWrapper";
import { FieldsToLookupByMasterAttributeId, IFlexibleFieldWithAttributes, calcFieldName, friendlyValue } from "global/Application";
import { AppDataSource } from "global/DataSources/AppDataSource";
import Formatters from "global/Utility/Formatters";
import { ArrayToLookup } from "global/Utility/TSFunctions";
import {
    IOpportunityProductOptionBenefit as FAM_IOpportunityProductOptionBenefit
} from "model/FlexibleAttributeModel";
import { EColumnExtractDataTypes } from "./EColumnExtractDataTypes";
import { IColumnExtract } from "./IColumnExtract.type";
import { IDatasetExtract } from "./IDatasetExtract";
import { ExcelStyles } from "./ExcelStyles";
import { IRowExtract } from "./IRowExtract.type";

interface IRowExtractExtended extends IRowExtract {
    [key: string]: string | undefined | { value: string, format: string } | any;
    _config?: IFlexibleFieldWithAttributes | undefined;
    bcbs_label: string | undefined;
    _property?: IPropertyAttribute<any> | undefined;
}

interface IPropertyAttribute<T> {
    index: number;
    logicalName: keyof T;
    label: string;
    type: EControlType;
    defaultValue: string;
}

interface IOptions {
    options: FAM_IOpportunityProductOptionBenefit[];
    isCompare?: boolean;
}

export default async function OpportunityProductBenefitCompareGeneration(state: RootState, dataSource: AppDataSource, data: IOptions) {
    const rows: IRowExtractExtended[] = [];
    const rowsLookup: Record<string, IRowExtractExtended> = {};

    const columns: Record<string, IColumnExtract> = {};
    let columnIX = 0;
    const config = state.data.config;

    function removeNonStandardCharacters(value: string): string {
        return value.replace(/[^a-zA-Z0-9 ]/g, "");
    }


    // properties ----------------------------------------------------------------------------------------------
    const properties: Record<string, IPropertyAttribute<FAM_IOpportunityProductOptionBenefit>> = {
        'name': {
            index: 0,
            logicalName: 'name',
            label: 'Name',
            type: EControlType.text,
            defaultValue: '-',
        },
        'deactivated': {
            index: 1,
            logicalName: 'deactivated',
            label: 'Deactivated',
            type: EControlType.boolean,
            defaultValue: 'false',
        },
        'bcbs_buyupuwsinglei': {
            index: 2,
            logicalName: 'bcbs_buyupuwsinglei',
            label: 'Enrollment - Single',
            type: EControlType.wholeNumber,
            defaultValue: '-',
        },
        'bcbs_buyupuwfamilyi': {
            index: 2,
            logicalName: 'bcbs_buyupuwfamilyi',
            label: 'Enrollment - Family',
            type: EControlType.wholeNumber,
            defaultValue: '-',
        },
        'bcbs_buyupuweechildreni': {
            index: 3,
            logicalName: 'bcbs_buyupuweechildreni',
            label: 'Enrollment - EE/Children',
            type: EControlType.wholeNumber,
            defaultValue: '-',
        },
        'bcbs_buyupuweespousei': {
            index: 4,
            logicalName: 'bcbs_buyupuweespousei',
            label: 'Enrollment - EE.Spouse',
            type: EControlType.wholeNumber,
            defaultValue: '-',
        },
        'bcbs_buyupuwtotali': {
            index: 5,
            logicalName: 'bcbs_buyupuwtotali',
            label: 'Enrollment - Total',
            type: EControlType.wholeNumber,
            defaultValue: '-',
        },
        'bcbs_buyupuwsinglem': {
            index: 6,
            logicalName: 'bcbs_buyupuwsinglem',
            label: 'Rates - Single',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
        'bcbs_buyupuwfamilym': {
            index: 7,
            logicalName: 'bcbs_buyupuwfamilym',
            label: 'Rates - Family',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
        'bcbs_buyupuweechildrenm': {
            index: 8,
            logicalName: 'bcbs_buyupuweechildrenm',
            label: 'Rates - EE/Children',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
        'bcbs_buyupuweespousem': {
            index: 9,
            logicalName: 'bcbs_buyupuweespousem',
            label: 'Rates - EE/Spouse',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
        'bcbs_buyupuwtotalm': {
            index: 10,
            logicalName: 'bcbs_buyupuwtotalm',
            label: 'Rates Total',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
        'bcbs_annualpremium': {
            index: 11,
            logicalName: 'bcbs_annualpremium',
            label: 'Annual Premium',
            type: EControlType.currency,
            defaultValue: '$--.--',
        },
    }

    // First Column
    // columns ---------------------------
    columns['bcbs_label'] = {
        attributeId: '',
        order: 0,
        width: 35,
        index: ++columnIX,
        fieldName: 'bcbs_label',
        hidden: false,
        name: 'Property',
        dataType: EColumnExtractDataTypes.Default,
    };

    // lets create fields
    data.options.forEach(eachOption => {

        const tiers = ArrayToLookup(eachOption?.bcbs_tierdefinition?.tierLevels ?? []);

        let name = removeNonStandardCharacters(eachOption.name!);

        // if name already exists, we need to add a number to it
        if (columns[name]) {
            name = name + ' ' + Object.keys(columns).filter(key => key.startsWith(name)).length;
        }

        // add a new column
        columns[name] = {
            attributeId: '',
            order: 0,
            width: 35,
            index: ++columnIX,
            fieldName: name,
            hidden: false,
            name: name,
            dataType: EColumnExtractDataTypes.Default,
        };

        const fieldsByMasterAttId = FieldsToLookupByMasterAttributeId(config, eachOption.fields);

        // Fields ---------------------------
        Object.values(fieldsByMasterAttId)
            .sort((a, b) => a[0].index - b[0].index)
            .forEach(eachField => {

                // we know first one is the one with value
                const eachFieldFirst = eachField[0];
                eachFieldFirst.field.values.forEach((eachFieldValue, index) => {

                    let key = "";

                    if (eachFieldFirst.configAttribute.bcbs_label)
                        key = key + '/' + eachFieldFirst.configAttribute.bcbs_label;

                    if (eachFieldValue.tierRef?.id) {
                        key = key + '/' + tiers[eachFieldValue.tierRef?.id].bcbs_name;
                    }

                    if (eachFieldValue.type)
                        key = key + '/' + eachFieldValue.type;

                    let row = rowsLookup[key];

                    // new row ?
                    if (!row) {
                        row = {
                            bcbs_label: '?',
                            _config: eachFieldFirst,
                        };

                        row.bcbs_label = calcFieldName(eachFieldFirst.field,
                            eachFieldValue,
                            eachFieldFirst.configAttribute,
                            tiers,
                        )

                        rows.push(row);
                        rowsLookup[key] = row;
                    }

                    const controlType = eachFieldFirst.masterAttribute.bcbs_attributetypecode as EControlType;
                    if (eachFieldValue.value)
                        row[name] = friendlyValue(controlType, eachFieldValue.value)
                    else
                        row[name] = '-';
                })
            });

        // Properties ---------------------------
        Object.values(properties)
            .forEach(property => {

                let key = property.logicalName;

                let row = rowsLookup[key];

                // new row ?
                if (!row) {
                    row = {
                        bcbs_label: property.label,
                    };

                    rows.push(row);
                    rowsLookup[key] = row;
                }

                row[name] = property.defaultValue;
                switch (property.type) {
                    case EControlType.boolean:
                        if (eachOption[key] === true)
                            row[name] = "yes";
                        else if (eachOption[key] === false)
                            row[name] = "no";
                        break;

                    case EControlType.wholeNumber:
                        if (eachOption[key] !== undefined && eachOption[key] !== null)
                            row[name] = eachOption[key]?.toString();
                        break;

                    case EControlType.currency:
                        if (eachOption[key] !== undefined && eachOption[key] !== null)
                            row[name] = Formatters.ToCurrencyUSD(eachOption[key] as number);
                        break;

                    case EControlType.text:
                        if (eachOption[key] !== undefined && eachOption[key] !== null)
                            row[name] = eachOption[key];
                        break;

                    default:
                        throw new Error(`Unknown property type ${property.type}`);
                        break;
                }

            })
    });

    if (data?.isCompare) {
        columns['change'] = {
            attributeId: '',
            order: 0,
            width: 35,
            index: ++columnIX,
            fieldName: 'change',
            hidden: false,
            name: 'Change',
            dataType: EColumnExtractDataTypes.Default,
        };

        columns['changeValues'] = {
            attributeId: '',
            order: 0,
            width: 100,
            index: Object.keys(columns).length,
            fieldName: 'changeValues',
            hidden: false,
            name: 'Distinct Values',
            dataType: EColumnExtractDataTypes.Default,
        };
    }

    const excludeKeys: (keyof IRowExtractExtended)[] = ['bcbs_label', 'change', 'changeValues'];

    const comparableColumns = Object.values(columns)
        .filter(t => !excludeKeys.includes(t.fieldName));

    rows.forEach(row => {
        row['change'] = 'no';


        delete row._config;
        delete row._property;

        // Method A -----------------------------------------------------------------
        // compare by only keys within a rwo
        // const keys = Object.keys(row);
        // const compareKeys = keys.filter(key => excludeKeys.includes(key) === false);
        // const compareValues = compareKeys.map(key => row[key] ?? '(undefined)');
        // const allValuesEqual = compareValues.every(value => value === compareValues[0]);
        // if (!allValuesEqual)
        //     row['change'] = {
        //         format: {
        //             styleIndex: 5,
        //         },
        //         value: 'yes'
        //     };


        // Method B -----------------------------------------------------------------
        // compare by all keys
        const rowDistinctValues: Record<string, any> = {};
        comparableColumns.forEach(eachColumn => {
            row[eachColumn.fieldName] = row[eachColumn.fieldName] ?? '-';
            const fieldValue = row[eachColumn.fieldName].toString() ?? '(undefined)';
            rowDistinctValues[fieldValue] = fieldValue;
        });

        if (Object.keys(rowDistinctValues).length > 1) {
            row['change'] = {
                format: {
                    styleId: 'yellow'
                },
                value: 'yes',
            };
            row['changeValues'] = Object.values(rowDistinctValues)
                .sort((a, b) => a.localeCompare(b))
                .join(', ');
        }
    })

    const columnsArray = Object.entries(columns).map(kv => kv[1]);

    const dataSet: IDatasetExtract = {
        name: '',
        tables: [
            {
                columns: columnsArray,
                data: rows,
                name: "Product Options"
            }
        ],
        dropDownTables: null,
        styles: { ...ExcelStyles, fontSize: 10 }
    }

    const getNonstandardExtractAsync = await dataSource.getGenerateExcelExtract(dataSet);
    return getNonstandardExtractAsync;

}